import { FEATURES } from '@/config/featureConfig';

// LockInfo interface for menu items
interface LockInfo {
  requiredPackage: 'Professional' | 'Replenishment';
  benefits: string[];
  tooltipText?: string;
}

/**
 * Links from the v2 sidebar that v3 exposes through the Settings hub
 * (/assistant/setup) instead of sidebar items. Contract with
 * atlas/frontend/src/app/setup/components/SettingsSidebar.tsx — its nav test
 * asserts the same list. Keep in sync with the parity unit test.
 */
export const SETTINGS_HUB_COVERED: string[] = [
  '/settings/reorder-parameters',
  '/settings/incoming-inventory',
  '/settings/inventory-forecast',
  '/settings/bundle-product-mapping',
  '/settings/multi-store-settings',
  '/settings/location-restrictions',
  '/settings/purchase-order',
  '/settings/internal-inventory-transfer-settings',
  '/automated-po-overview',
  '/automation-configs',
  '/schedules',
  '/schedule/profiles',
  '/schedule/templates',
  '/email-templates',
  '/email-receivers',
  '/google-spreadsheet-introduction',
  '/google-spreadsheet-templates',
  '/google-spreadsheet-files',
  '/shopify-flow',
  '/shopify-flow/templates',
  '/shopify-flow/events',
  '/stocky-switcher',
  '/stocky-data-migration',
  '/subscription',
  '/users',
  '/importing-status',
  '/logout',
];

/**
 * Routes deliberately ABSENT from the converged v3 sidebar but alive at their
 * URLs (Release A, spec docs/superpowers/specs/2026-08-05-menu-convergence-design.md):
 * the classic forecast/reorder twins whose jobs the Buy group now answers
 * once. The parity test treats these as reachable; the pages stay routable,
 * permission-gated, and maintained. analyticsV3 (paid non-replenishment)
 * still shows them — they are that tier's only ACCESSIBLE forecasting.
 */
export const INTENTIONALLY_UNLINKED: string[] = [
  '/reorder-suggestions',
  '/inventory-forecasting',
  '/sales-forecast',
];

/** The classic twins, kept ONLY in the analytics-tier shape (see analyticsV3). */
const CLASSIC_INSIGHT_TWINS = [
    {
        title: 'Reorder Analysis',
        icon: 'mdi-chart-bar',
        type: 'single',
        permissionRequired: true,
        permissionCode: FEATURES.PAID_MEASURES,
        lockInfo: {
            requiredPackage: 'Professional',
            benefits: [
                'Comprehensive reorder analysis',
                'Optimal order quantities',
                'Lead time considerations',
                'Cost optimization'
            ],
            tooltipText: 'Detailed analysis for smart reordering'
        } as LockInfo,
        link: '/reorder-suggestions',
    },
    {
        title: 'Forecast Future Demand',
        icon: 'mdi-crystal-ball',
        type: 'single',
        permissionRequired: true,
        permissionCode: FEATURES.DEMAND_FORECASTING,
        lockInfo: {
            requiredPackage: 'Professional',
            benefits: [
                'AI-powered demand forecasting',
                'Seasonal trend analysis',
                'Accurate inventory planning',
                'Reduce overstock and stockouts'
            ],
            tooltipText: 'Predict future inventory needs'
        } as LockInfo,
        link: '/inventory-forecasting',
    },
    {
        type: 'single',
        icon: 'mdi-calendar-month',
        title: 'Monthly sales forecast',
        permissionRequired: true,
        permissionCode: FEATURES.DEMAND_FORECASTING,
        lockInfo: {
            requiredPackage: 'Professional',
            benefits: [
                'Monthly sales predictions',
                'Budget planning support',
                'Inventory optimization',
                'Cash flow management'
            ],
            tooltipText: 'AI-powered monthly sales forecasting'
        } as LockInfo,
        link: '/sales-forecast',
    },
];

export const replenishmentV3 = {
    data: [
        // Carried over verbatim from replenishment-v2. Migrating merchants need
        // this reachable without going through the Settings hub; the hub keeps
        // its own entry too (see SETTINGS_HUB_COVERED above), so the two are
        // additive rather than a move.
        {
            section: 'stocky-migration',
            title: 'Switching from Stocky',
            icon: 'mdi-swap-horizontal',
            type: 'group',
            items: [
                {
                    title: 'Master Guide',
                    icon: 'mdi-book-open-page-variant-outline',
                    type: 'single',
                    link: '/stocky-switcher',
                },
                {
                    title: 'Stocky Migration Tool',
                    icon: 'mdi-database-import-outline',
                    type: 'single',
                    link: '/stocky-data-migration',
                },
            ],
        },
        {
            section: 'today',
            title: 'Today',
            icon: 'mdi-weather-sunny',
            type: 'single',
            link: '/assistant/today',
        },
        {
            section: 'order',
            title: 'Buy',
            icon: 'mdi-cart-plus',
            type: 'group',
            permissionRequired: true,
            permissionCode: FEATURES.PAID_MEASURES,
            lockInfo: {
                requiredPackage: 'Replenishment',
                benefits: [
                    'Create purchase orders',
                    'Analyze reorder points',
                    'Forecast future demand',
                    'Automate ordering process',
                    'Manage supplier relationships'
                ],
                tooltipText: 'Advanced ordering and purchase management'
            } as LockInfo,
            items: [
                {
                    title: 'Forecasting',
                    icon: 'mdi-chart-timeline-variant',
                    type: 'single',
                    link: '/assistant/forecast',
                    permissionRequired: true,
                    permissionCode: FEATURES.BUYING_TABLE,
                    lockInfo: {
                        requiredPackage: 'Replenishment',
                        benefits: [
                            'Demand forecasts from sales history',
                            'What-if trend scenarios',
                            'Seasonality-aware projections',
                            'Stockout and overstock risk'
                        ],
                        tooltipText: 'Forecast demand and plan ahead'
                    } as LockInfo,
                },
                {
                    title: 'Demand Plans',
                    icon: 'mdi-calendar-month',
                    type: 'single',
                    badge: 'Beta',
                    link: '/assistant/plans',
                    permissionRequired: true,
                    permissionCode: FEATURES.BUYING_TABLE,
                    lockInfo: {
                        requiredPackage: 'Replenishment',
                        benefits: [
                            'Commit monthly demand plans',
                            'Plan new-product launches from references',
                            'Create purchase orders from a plan',
                            'Track plan vs actual sales'
                        ],
                        tooltipText: 'Plan demand and track attainment'
                    } as LockInfo,
                },
                {
                    title: 'Reorder',
                    icon: 'mdi-table',
                    type: 'single',
                    link: '/buying-table',
                    permissionRequired: true,
                    permissionCode: FEATURES.BUYING_TABLE,
                    lockInfo: {
                        requiredPackage: 'Replenishment',
                        benefits: [
                            'Interactive buying table',
                            'Bulk order creation',
                            'Real-time calculations',
                            'Supplier comparison',
                            'Quick order adjustments'
                        ],
                        tooltipText: 'Create orders from interactive buying table'
                    } as LockInfo,
                },
                {
                    title: 'Suppliers',
                    icon: 'mdi-account-group',
                    type: 'single',
                    link: '/suppliers',
                },
                {
                    title: 'Buying Calendar',
                    icon: 'mdi-weather-sunny',
                    type: 'single',
                    link: '/buying-calendar',
                    permissionRequired: true,
                    permissionCode: FEATURES.BUYING_CALENDAR,
                    lockInfo: {
                        requiredPackage: 'Replenishment',
                        benefits: [
                            'Analyze what to order',
                            'Optimize inventory levels',
                            'Reduce stockouts and overstock',
                            'Save time on manual analysis'
                        ],
                        tooltipText: 'Analyze what to order'
                    } as LockInfo,
                },
            ],
        },
        {
            section: 'track',
            title: 'Track',
            icon: 'mdi-truck-fast',
            type: 'group',
            items: [
                {
                    title: 'Purchase Orders',
                    icon: 'mdi-clipboard-list',
                    type: 'link',
                    link: '/purchase-orders',
                    permissionRequired: true,
                    permissionCode: FEATURES.PURCHASE_ORDERS,
                    lockInfo: {
                        requiredPackage: 'Replenishment',
                        benefits: [
                            'View all purchase orders in one place',
                            'Track order status and deliveries',
                            'Manage supplier relationships',
                            'Export order history'
                        ],
                        tooltipText: 'View and manage existing purchase orders'
                    } as LockInfo,
                },
                {
                    title: 'Needs Approval',
                    icon: 'mdi-timer-sand',
                    type: 'single',
                    link: '/purchase-orders/unapproved',
                    permissionRequired: true,
                    permissionCode: FEATURES.AUTOMATED_PURCHASE_ORDER,
                    lockInfo: {
                        requiredPackage: 'Replenishment',
                        benefits: [
                            'Automatically generate purchase orders',
                            'Review and approve orders before sending',
                            'Save time on manual order creation',
                            'Reduce stockout risks'
                        ],
                        tooltipText: 'Review and approve automated purchase orders'
                    } as LockInfo,
                },
                {
                    // Last in Track (user decision 2026-08-14): day-to-day PO work
                    // first, the analytics view after it.
                    title: 'PO Insights',
                    icon: 'mdi-chart-box-outline',
                    type: 'single',
                    link: '/assistant/purchase-order/insights',
                    permissionRequired: true,
                    permissionCode: FEATURES.PURCHASE_ORDERS,
                    lockInfo: {
                        requiredPackage: 'Replenishment',
                        benefits: [
                            'Track open and overdue purchase orders',
                            'Compare ordered vs received quantities',
                            'Score supplier on-time delivery and fill rate',
                            'Analyze purchasing spend and lead times'
                        ],
                        tooltipText: 'Reports on PO status, receiving, suppliers and spend'
                    } as LockInfo,
                },
            ],
        },
        // Physical-inventory operations — counting and moving stock — as their own
        // workflow, not a tracking activity (user decision 2026-07-18) and not an
        // ordering one (user decision 2026-07-20): a stock take creates no order, so
        // it belongs beside transfers rather than in Order.
        //
        // Ranked AFTER Track (user decision 2026-07-20) so Order -> Track reads as one
        // uninterrupted purchase-order lifecycle (decide what to buy, then follow those
        // orders), and because these are periodic operations — a stock take is monthly
        // or quarterly — which should not outrank daily work in the sidebar.
        //
        // Transfer Rules stays in SETTINGS_HUB_COVERED too: the hub list is a wire
        // contract with Atlas's SettingsSidebar and dual reachability is intentional.
        {
            section: 'inventory',
            title: 'Inventory',
            icon: 'mdi-swap-horizontal',
            type: 'group',
            items: [
                {
                    // Counting comes before moving: you correct what you have, then
                    // decide what to shift between locations.
                    title: 'Stock Take',
                    icon: 'mdi-clipboard-list-outline',
                    type: 'single',
                    link: '/assistant/stock-take',
                    permissionRequired: true,
                    permissionCode: FEATURES.BUYING_TABLE,
                    lockInfo: {
                        requiredPackage: 'Replenishment',
                        benefits: [
                            'Count inventory with your phone',
                            'Import scanner-device counts',
                            'See shrinkage in dollars',
                            'Push corrected stock to Shopify'
                        ],
                        tooltipText: 'Count physical inventory and reconcile with Shopify'
                    } as LockInfo,
                },
                {
                    // Correcting comes straight after counting: a stock take finds the
                    // discrepancy, an adjustment writes it off with a reason.
                    // "Adjustments", not "Inventory Adjustments": the item sits
                    // inside the Inventory group, so the prefix is redundant —
                    // Stock Take isn't "Inventory Stock Take" either. It also
                    // keeps the label the same length as its badged siblings;
                    // v-list-item-title clips title + chip as ONE nowrap line,
                    // so a longer label eats its own Beta badge.
                    title: 'Adjustments',
                    icon: 'mdi-pencil-ruler',
                    type: 'single',
                    badge: 'Beta',
                    link: '/assistant/inventory-adjustments',
                    permissionRequired: true,
                    permissionCode: FEATURES.BUYING_TABLE,
                    lockInfo: {
                        requiredPackage: 'Replenishment',
                        benefits: [
                            'Write off damage and loss with a reason',
                            'See every adjustment in dollars',
                            'Permanent audit history'
                        ],
                        tooltipText: 'Record stock write-offs and corrections with a full audit trail'
                    } as LockInfo,
                },
                {
                    title: 'Transfer Suggestions',
                    icon: 'mdi-lightbulb-on',
                    type: 'single',
                    link: '/inventory-transfers/suggestions',
                    permissionRequired: true,
                    permissionCode: FEATURES.REPLENISH_FROM_WAREHOUSE,
                    lockInfo: {
                        requiredPackage: 'Professional',
                        benefits: [
                            'Get AI-powered transfer suggestions between locations',
                            'Reduce manual stock movement planning',
                            'Optimize inventory levels across all warehouses',
                            'Minimize out-of-stock and overstock situations'
                        ],
                        tooltipText: 'Get transfer suggestions'
                    } as LockInfo,
                },
                {
                    title: 'Transfer Rules',
                    icon: 'mdi-cog',
                    type: 'single',
                    link: '/settings/internal-inventory-transfer-settings',
                    permissionRequired: true,
                    permissionCode: FEATURES.REPLENISH_FROM_WAREHOUSE,
                    lockInfo: {
                        requiredPackage: 'Replenishment',
                        benefits: [
                            'Set up rules for transferring inventory',
                            'Reduce manual stock movement planning',
                            'Optimize inventory levels across all warehouses',
                            'Minimize out-of-stock and overstock situations'
                        ],
                        tooltipText: 'Set up rules for transferring inventory'
                    } as LockInfo,
                },
            ],
        },
        // Automation is a product pillar, not just configuration (user decision
        // 2026-08-05; spec docs/superpowers/specs/2026-08-05-automate-menu-group-design.md):
        // ~45 merchants/month reached the Automated-PO surface through the
        // Settings hub alone, so the surfaces earn sidebar slots. Links use
        // CLASSIC paths — VerticalMenuBar rewrites them via ATLAS_FEATURE_LINKS
        // when the per-feature toggle is 'atlas' (the shipped default), which
        // keeps the classic⇄atlas switch and the deliberately-unmapped
        // /schedule/create hand-off working. Ranked after Inventory so the
        // operational groups stay contiguous and set-up-once automation does
        // not outrank daily work. Items gate individually: v2 gated the parent
        // sub-groups, and a flattened single item must carry that gate itself
        // or the feature leaks to unentitled merchants. The Settings-hub
        // entries stay (SETTINGS_HUB_COVERED untouched) — dual reachability,
        // same as Transfer Rules and Stocky. Google Sheets Sync and email
        // templates stay hub-only: 6 and 14 uniques/90d don't earn slots.
        {
            section: 'automate',
            title: 'Automate',
            icon: 'mdi-assistant',
            type: 'group',
            items: [
                {
                    title: 'Automated POs',
                    icon: 'mdi-file-document-refresh',
                    type: 'single',
                    link: '/automated-po-overview',
                    permissionRequired: true,
                    permissionCode: FEATURES.AUTOMATED_PURCHASE_ORDER,
                    lockInfo: {
                        // NOT v2's 'Professional': automation is Replenishment-exclusive
                        // (docs/package-rules-catalog.md:73,137 — automated_purchase_order
                        // is unlocked on RC only), and the Track group's "Needs Approval"
                        // entry above quotes the same gate. Quoting Professional would sell
                        // a plan that still leaves the feature locked.
                        requiredPackage: 'Replenishment',
                        benefits: [
                            'Automate purchase order creation',
                            'Reduce manual data entry',
                            'Streamline supplier communication',
                            'Improve order accuracy and efficiency'
                        ],
                        tooltipText: 'Unlock automated purchase order creation and streamlined workflows'
                    } as LockInfo,
                },
                {
                    title: 'Scheduled Reports',
                    icon: 'mdi-file-clock',
                    type: 'single',
                    link: '/schedules',
                    permissionRequired: true,
                    permissionCode: FEATURES.SCHEDULE_MANAGEMENT,
                    lockInfo: {
                        requiredPackage: 'Professional',
                        benefits: [
                            'Automate delivery of scheduled reports',
                            'Improve team communication with regular updates',
                            'Save time on manual report distribution',
                            'Ensure stakeholders receive timely information'
                        ],
                        tooltipText: 'Automate and schedule report delivery for your team'
                    } as LockInfo,
                },
                {
                    title: 'Shopify Flow',
                    icon: 'mdi-arrow-decision-auto',
                    type: 'single',
                    link: '/shopify-flow',
                    permissionRequired: true,
                    permissionCode: FEATURES.SHOPIFY_FLOW_INTEGRATION,
                    lockInfo: {
                        requiredPackage: 'Replenishment',
                        benefits: [
                            'Automate inventory workflows with Shopify Flow',
                            'Trigger actions when POs are created or updated',
                            'Send notifications to Slack, email, and more',
                            'Schedule report insights like low stock or reorder SKUs',
                            'Access pre-built workflow templates'
                        ],
                        tooltipText: 'Connect Assisty events to Shopify Flow automations'
                    } as LockInfo,
                },
            ],
        },
        {
            section: 'insights',
            title: 'Insights',
            icon: 'mdi-chart-line',
            type: 'group',
            // Grown from 3 boards to 8 entries (user decision 2026-08-18). The
            // converged v3 menu had dropped the classic Analytics section wholesale
            // (analytics-v1.ts:128), leaving the highest-paying tier with three
            // dashboards standing in for a 26-report catalogue.
            //
            // Ordered by the question a merchant asks, not by report taxonomy:
            // how is the business doing -> is my stock healthy -> what do I reorder
            // -> what is it costing me -> where do I focus.
            //
            // Deliberately NOT here: Report Library and AI Report Builder. Both
            // already render as fixed top-level shortcuts above every section
            // (VerticalMenuBar.vue:75,81 — "AI Report Builder" and "Browse
            // Reports"), so repeating them inside Insights would put the same
            // destination in the sidebar twice. Those shortcuts are the doors onto
            // everything this list does not name.
            //
            // Ungated like the three boards they join. The underlying measures
            // (aging_stock, sell_through_rate, stock_availability,
            // inventory_to_sales_ratio) are all in featureConfig's
            // PREMIUM_MEASURE_CODES, so the paid_measures fallback resolves them for
            // paid packages and the server enforces the real boundary.
            items: [
                {
                    title: 'Business Overview',
                    icon: 'mdi-view-dashboard', // dashboard/overview icon
                    type: 'single',
                    link: '/board/117',
                },
                {
                    title: 'Sales Performance',
                    icon: 'mdi-trending-up',
                    type: 'single',
                    link: '/board/64',
                },
                {
                    title: 'Inventory Performance',
                    icon: 'mdi-warehouse', // inventory/warehouse icon
                    type: 'single',
                    link: '/board/2333',
                },
                {
                    // One board standing in for four rows — turnover, sell-through,
                    // stock availability, inventory-to-sales — which is the biggest
                    // breadth-per-row win available here.
                    title: 'Inventory Metrics',
                    icon: 'mdi-speedometer',
                    type: 'single',
                    link: '/board/337',
                },
                {
                    // Report Library id 'reorder-analysis'. Deliberately NOT
                    // '/reorder-suggestions', the classic twin the converged menu
                    // keeps in INTENTIONALLY_UNLINKED: both resolve to the same
                    // route but carry a different report_query, and this one is the
                    // read-only analysis behind Buy > Reorder rather than a second
                    // buying surface.
                    //
                    // Titled "Reorder Analysis Report" — the Report Library's own
                    // name — NOT the bare "Reorder Analysis" the twin used. Reusing
                    // the retired label would show merchants the removed wording
                    // again and defeat the convergence guard in
                    // e2e/tests/smoke/menu-convergence.spec.ts.
                    title: 'Reorder Analysis Report',
                    icon: 'mdi-chart-bar',
                    type: 'single',
                    link: '/analytics/inventory-replenishment/reorder-analysis?report_query=eJztWW1v2zYQ_iuCgAEtYhW2bOfFGAa0bgYUa9o0STMMQSAwEp1wpUSVpNwYQf77jpRtnmTZUxyg7QZ_sU3yubuHd3w5nh98zTSn_sh_l01ppoWceWc05zRj6i6FDr_jx3dE6otZblDQvCNqbHr8kZYF7fgJUzknsxORGIAmN6Cu4yvKaaxpMiYyGQtepJnyR1cPfkZSAzuVIiliq72Ue6dpemU-vLdUxZLlmonsGsYTosncuKb3RoIXTJ2QPGfZLXTezKJ8ruyxs9R_SSQjGdI_XXb8m8IFEmk7_-Oz0zS3FqkvxZPoWQGk9IwKmVDpfSrAGtMzZ-GKLWIRSRyLmjsSGrOU8BWTa6SbbL8lmiK7suyOwAitGyuRVUsYj9V_zphW3rngCVKuoBl9nU82yokkacsJNUiuWjMgL4c5JQS7kkypJLcUKDI-iwoDjay-pxDYrARzeZ38XShY9l6xFSlFOFWRljRLnksNqcIE31OSeBcspd4L4KReIlawahIhIw6ISAOiJYO6WGXjkAnVM-9ci_gLXgq2O1KmGwSnlLddCSuCTav6VDC89ZfLOjf9LS1VZLARd042bNzXsS4I91YxLa2ula8yiEUK-OaToxyL3CHw9WkUNmjAJN7C4vHExMPXRkpYZpSgQNtAmYWG1MFtkRlc25hv0oEpnYD1tEibvOcYwdS2902zcIUDuW_Bgdw_g0OjcJMf7K7zxmJqD_qZqnmh9Gtshq13n-CDumiTBzZZhylsa71BtGnuH9ddrYZ-ubO3iH1VsGnW6-0C8e3srghiu7-ze7hs1lqdmOHt7DaJNh2241nMm3KI2PQ_8bC1MvWrHdaQwqf54sYzdx2sAlU_0eNCwqUXz9ZelUtB__G646eUqELSDXmTSXHhh4KEtEzoXC6pNCTC5a038sNuOAy6w6A3gBG4FKv9h0Gvb1TRCSm4jiTJbs1gP4XOCeOaSsiPgU_OpkJHwua_ZcJtk4xK9nx-J755FyJ3XtEiz6A1JbyA5pX_mnP_et6e-0WBF2ykGlJWpvSlgZZWWOKPhvsd_z25gQt25A8Bcllqht8mPBZx4BC9LoJAY4k5dJgQY0KEOUKWMGboMPtdhzFTcyDTMmHkgiQ2zBPCFUVL6Pg-5oUZ8kShAzEJ7PHhJZUjCYYiMYnQVUMXYtirmXieU_d7bhofBJoFKF5ONXSYv6hCoBm0Nk51mXxe2KxvOTucCqLZ9OqTMYyrU8mK9AYWZuNkUGj3et3uLzi6r1zoUHT3jiqo3qujJaqHArx3WIMdOhhy4N5BDXbgYMiHe_s12L6D9RFsWIO5hd4bINigBhs42BDB-jVY38HQxtoLa7DQwQ4q7q3Ceg6Go1BFOQwKQVBV1UUhCFEIgrAGcyEIUQiCfg3mQhCiEASDGsyFIEQhCIY1mAtBiEIQ7NdgLgQhCkFwUIO5EIQoBMFhDeZCEKIQBEc1WK9pK167uoe5Q4Q098y62of3LoXbyO1SZptoO-9qJD_BY2v5qll9L3_Xx82uWrSrFu2qRf_BatFPUKvZFUZ2hZFdYeR_VRi5tMeo0162vQ9mtFXCVZ7D37PW4ix9xA_x2hZYeYm39NSKXEMm7cH3BCZ5QuQty5DRRfaZ2_EoteM1w3Avx2U1qGq4Uba6HlBahc6_mqsr2Rc6yJ7i9U061lM6g5sxK2gLLpJqSBwiWzN4Liesaz23MmItqN1KodQ8CM-lhnXZZ15ulnJZkoOX3INtw7u64zN4jKlTKk9txxBe0AoefW9mpryyPi-_LmHmEQdA8y869IDRIreSi8Z83NQoIUs8F-Yvd_vONB1cM9TzCA4iPC44MU_PlGrJYlXKQoYAeSdTIisT79GDAQsu5FnB5-Wc8pE6bvuumVeQ4HksYFcQGAexX_25WuPY25sX4XDY8cLuwH68XAyChnlUpPjmt6m-mqWxHb-rg05vcF0h-eb44s_j4w-NVAdd-_FjqPYGnX63Fde-oWkJ938MVyCAaf62Pu7D4XMI2lp4Np5zLKBr1H_8B5_44M0',
                },
                {
                    title: 'Lost Revenue',
                    icon: 'mdi-cash-remove', // cash loss/stockout icon
                    type: 'single',
                    link: '/board/372'
                },
                {
                    // The overstock twin of Lost Revenue: understock cost sits
                    // above, trapped cash here. Report Library id 'aging-stock'.
                    title: 'Aging & Dead Stock',
                    icon: 'mdi-clock-alert-outline',
                    type: 'single',
                    link: '/reports/562?report_query=eJzdVttuGjEQ_ZWVnzcS0JI2vBHy0KitSkuSFxStjNdZrHjtrS-0CPHvnfEurLmpSSVUtS8Iz-Ucezw-syvihJOcDMiwEKpIJk6z52RsdO6ZsyQlbE6Nu1tWGALLObUjtJCBM56nxM71j8bwRKVFC5ecOZ6PqMlHWvpSWTKYroiiJWI00IisczTcOl5O8Se54ZYZUTmh1SP4c-poQ-z4T8yQXtjPtKpgo2CcLbOqAVunW_wHagRVEf5ia_gNoPJSRkCTj_ctSEOU2Wf_qp2FhAj0mpqAeAA82zpeAb5Jigia8iZjP5PCznme3FB3hK_a-LO89ke0jWWX9kRiRH2vhLPJRMu8pZt6NGYWjHtXmnMmSioPeNoEcFEpqMXejHnqFv2ikg9UxVRD5jyVya1acOW0WSZfPdy7cMsXMp_MP7WRT9S65BtnXCwO6jw1jT2UaX8HRwq813272KG0EbgEb7Zl-P7iIx7knTrasAgnWsakW9semfLljJvDckYQRznwUMlIW7fXLhkD2x4H88ZwxZZH2yUknOwWLiUK29gIFt_Pjv2FbHhHOzSPKSk5td5gFkUBzSx2J8KJkisLUlY_2VaFrAO1zJwIu-t1em8vut2LXgc8XOW79t5F9wqh-BP1Ei6OqgKdbzrY9k9COih7JK1_9DAWVHqImpIOgdPoihsKMZBWuOM9hYeuxEK7TAeprudCRQ0td4R-ApMhudNVuxGnKxUzDqVEzrBuSCyUUJ5QQWHdA4bWLCIng_5lCg9lxiVE9SHkoUaG_9gAIeJdG9HtRCGw2Ma8b2N6cUwvirmKmOKYfhtz2Wlj8GhtEK6wbFLTPDRSGJVo2QxLeJSVNtjJ_-7A_B_k8FwSeM4x-VdH4_n0_IwaHiSMFrxRMHhuq7Amg25KBLwsO-ZmHAx9eNQWXub1EhUrboDH2oFvEFz4NQyWwmhfhdjNovHjpPDWTXT0pVyCqIvIsk7b70xewPhwGeoK7IBRybykKBQld3BaW0MyXYLwCqtV_TU2WK3XvwCqa_az',
                },
                {
                    // Report Library id 'abc-analysis'.
                    title: 'ABC Analysis',
                    icon: 'mdi-chart-donut',
                    type: 'single',
                    link: '/reports/672?report_query=eJztWW1v2zYQ_iuCgAEbZhW2HDuJv6XuNhRru6xOAwxBIDASnXClRJWk3AhB_vuOlG2eaNm1HQzYsH0JwuNzb7zj3VF-CjXTnIaT8OL1NGDFghZayDogBeG1YirshekDkfqqLg0Ilg9ETQ0lnGhZ0V6oHsTXJWFOuDIUymmqaTYlMpsKXuWFCic3T2FBciPjUoqsSrWRLDJDeKtpfmP-BG-oSiUrNRPFLexnRJOlYk0fDQevmHpPypIV90C8q5NyKey5t5Z_TSQjBZK_WBO-JXCFRNJmv35ykpbaEvW5Osg8y4CEfqRCZlQGv1egjenaabhZhyCRtOS0YOohB4J3HBlNWU74hsot3F263xBNkV7ZkBNQQn1lDbKtCeOx-E8F0yqYCZ4h4QqWyZels0lJJMn3dKiDc1ObAQUl-JQRfJRkQSW5p2Ai43VSGWhi5R1iwG4h2JaL7M9KQdoH1VFGKcKpSrSkRfZS05AobOA7SrLgiuU0-B5sUj8gqyBrMiETDohEA2JPC3y21sUhc6rrYKZF-hmngiUnypCBcUH5vpmwwdiV1ZeC4au_TuvS0PfU1OLBSt6uy2PHxb1IdUV4sInZU-tW_rYFqcgB3105mr3EFYEvh5mwQwI24g0kTyDmyNSPNCesMEJQoG2gTKIhcSUnhcHtG_NdMrBJ70F7XuVdp-csAteOP5tu5pYN5HEPG8jjC2zoZO46B3vrgqlY2EJfK-8UmnNNzbY93QPOwGftOoFd2sGFY7V3sHb5_tu21mrMb272EbFvM3Z5vV0vGH6c3g1GrPdn9gjNZqvWudk-Tm8Xa1exndYp75ohUkM_sNhaHr-1Qw4pXM1XHc_0OsgC5Vf0tJLQ9NJ6a6tcM4bPt70wp0RVku6Ym0A6tLVCwUDaDHRullQaZt6m603CuB-fRIN-FA9hB5oioo-i_iCKYyOKzknFdSJJcW82hzkQ54xrKmE-BntKthA6EXb-bYZtO2S0puf1lHFl2_v6bHDP78Egyyug3oSD8Ha5Wh4RZ8qbhIsqvwMLenbr2kAbhSwLJ2e98B25g1Y7CX8c9PvfAeq6ER3Gr_o2XAZ2jmDnLdTg1fkaNegj2JkHO3OwAYKderBTB4sRbOzBxg42RLCRBxs52AmCnXiwEwcbIdjQgw0dbIxgsQeLHey0dbxt2MDBcBTaKIdBIYjaovooBDEKQRR7MBeCGIUgGnowF4IYhSA68WAuBDEKQTTyYC4EMQpBNPZgLgQxCkF06sFcCGIUgujMg7kQxCgE0bkHG9gqwQXJbBWxz1tUoX56THlltgJR6UjMI9udgqzV8WArEfMETTJ0xYbvaSH8i6oAYatrxzNz86KOUcQ-COQFCF75Okbh-oMqBKphtdNVmDuXrpbLgo1cXJNgOPk7XBvu4drJ0a7NHsTX4EqUzh8tygI7cMH5yzwYoVwcIdtc7o9QGg76-Ia7IjtCZSDGmBhhUBkYYczIYcaoBhjXHMisOs7q1n3SMe1RSNNC_-WfdZbWBr9IkuERZvXB5t7Q9zG2xYA1fKA6sE05SEWhJburbFvHL2HTsfGmp6-kMm2GEP8l7DP6emcGgVQVVDcfBfbUsMb_s16_xrX_7vel_9_-3W9_Z0pTx3Yk5fSAJ0Mnc_sVpCFmG0oxeV9dLVHfrCEBSdMqr3j7A-5mVUgA18COLixOhG0MpcnU5n0Ctf_JrmH27IUMyry6pPLSEkbQYRS0idd1864x_5seACvbUYB0L0VVrvbtYgkwzzJ44MwE-kEBLNAMUZ7Ba8JTa1eW5BTMTVXDC0kMF4cpMN1-mZ48PZt3VdH8DjEVFbg9GT7_BWoVZ9s',
                },
            ],
        },
        // User-created content keeps its own top-level home (user feedback
        // 2026-07-06): v2's My Workspace section carried over verbatim rather
        // than folded into Insights — saved reports/dashboards are things the
        // merchant MADE, and burying them under an analytics group cost the
        // one-click access they had in the old menu.
        {
            section: 'my-workspace',
            title: 'My Workspace',
            icon: 'mdi-home',
            type: 'group',
            items: [
                {
                    title: 'Saved Reports',
                    icon: 'mdi-file-document-multiple',
                    type: 'single',
                    link: '/my-reports',
                },
                {
                    title: 'Saved Dashboards',
                    icon: 'mdi-view-dashboard',
                    type: 'single',
                    link: '/my-dashboards',
                },
                {
                    title: 'Recent Downloads',
                    icon: 'mdi-download',
                    type: 'single',
                    link: '/download-manager',
                },
                {
                    title: 'Product segments',
                    link: '/product-segment',
                    icon: 'mdi-view-list',
                    permissionRequired: true,
                    permissionCode: FEATURES.PRODUCT_SEGMENTATION,
                },
            ],
        },
        // Companion Shopify apps by the Assisty team (spec 2026-10-05-companion-
        // apps-introduction-design.md F9). Embedded Atlas page; no gate.
        {
            section: 'companion-apps',
            title: 'More apps by Assisty',
            icon: 'mdi-apps',
            type: 'single',
            link: '/assistant/apps',
        },
        {
            section: 'settings',
            title: 'Settings',
            icon: 'mdi-cog',
            type: 'single',
            link: '/assistant/setup',
        },
    ]
}

/**
 * AS-10754 — the paid-non-replenishment (Professional/Analytics) v3 shape.
 *
 * This tier used to receive `replenishmentV3.data` with three twins appended,
 * i.e. an information architecture built for a Replenishment Center merchant:
 * the top three sections were the ones it cannot open, 11 of 34 rows were
 * padlocked, and `Track` expanded into three locked rows because it carried no
 * section-level gate. Insights and My Workspace — the two groups this tier pays
 * for — sat underneath all of it.
 *
 * The shape below is DERIVED from `replenishmentV3.data` by section key and
 * link rather than copied, so later edits to titles, icons or gates in the
 * converged menu still reach this tier. `pickSection` and `pickLinks` throw on
 * a miss, because the failure mode that matters is silent: a renamed link would
 * otherwise leave an empty group in a live sidebar.
 */

const findSection = (key: string): any => {
    const section = replenishmentV3.data.find((s: any) => s.section === key);
    if (!section) {
        throw new Error(`analyticsV3: replenishmentV3 has no section '${key}'`);
    }
    return section;
};

/** A section carried through unchanged. */
const pickSection = (key: string): any => findSection(key);

/**
 * A section narrowed to `links`, in the order given. Items keep their own
 * object identity, so gates and lock copy stay in step with the converged menu.
 */
const pickLinks = (key: string, links: string[]): any => {
    const section = findSection(key);
    const items = links.map((link) => {
        const item = section.items.find((i: any) => i.link === link);
        if (!item) {
            throw new Error(`analyticsV3: section '${key}' has no item linking to '${link}'`);
        }
        return item;
    });
    return { ...section, items };
};

/** Items gathered from several converged sections into the upsell group. */
const gatherLinks = (refs: Array<[string, string]>): any[] =>
    refs.map(([key, link]) => {
        const section = findSection(key);
        const item = section.items.find((i: any) => i.link === link);
        if (!item) {
            throw new Error(`analyticsV3: section '${key}' has no item linking to '${link}'`);
        }
        return item;
    });

/**
 * The classic twins, restored as their own group under the name this tier
 * already knows from replenishmentV2. `Reorder Analysis` is retitled to
 * `Reorder Analysis Report` — v2's pairing of that title with
 * `/reorder-suggestions` — because the Insights library row of the same name
 * is dropped below: `/reorder-suggestions` merely redirects to it, so the two
 * opened the same page under near-identical names.
 *
 * No section-level gate: a locked v3 section hides all its children, so gating
 * this group would only add a way to hide it during permission initialisation.
 * Both `paid_measures` and `demand_forecasting` resolve for any paid tier.
 */
const PLAN_DEMAND_SECTION = {
    section: 'plan-demand',
    title: 'Plan Demand',
    icon: 'mdi-chart-timeline-variant',
    type: 'group',
    items: [
        { ...CLASSIC_INSIGHT_TWINS[0], title: 'Reorder Analysis Report' },
        CLASSIC_INSIGHT_TWINS[1],
        CLASSIC_INSIGHT_TWINS[2],
    ],
};

/** The Insights library row that `/reorder-suggestions` already redirects to. */
const INSIGHTS_REORDER_LIBRARY_TITLE = 'Reorder Analysis Report';

const insightsWithoutDuplicateReorder = (): any => {
    const section = findSection('insights');
    const items = section.items.filter(
        (i: any) => i.title !== INSIGHTS_REORDER_LIBRARY_TITLE,
    );
    if (items.length === section.items.length) {
        throw new Error(
            `analyticsV3: Insights no longer holds a '${INSIGHTS_REORDER_LIBRARY_TITLE}' row to drop`,
        );
    }
    return { ...section, items };
};

/**
 * The 11 Replenishment-Center-only rows, collapsed into one locked group.
 *
 * Leans on behaviour v3 already has: a locked section hides its children and a
 * click opens the upgrade modal instead of expanding. So this is one padlocked
 * row rather than three groups that expand into dead ends. A store that DOES
 * hold `buying_table` simply sees the group expand, every item still carrying
 * its own gate — no capability is lost and no conditional is needed.
 *
 * `benefits` and `description` are repeated at the top level on purpose:
 * VerticalMenuBar spreads `...section` into the modal and reads them there,
 * not from `lockInfo`. (Reading `lockInfo` properly is a two-line fix in
 * VerticalMenuBar that would also repair the `Buy` section's empty modal for
 * Free merchants — its own ticket, per the description.)
 */
const REPLENISHMENT_UPSELL_BENEFITS = [
    'Reorder suggestions built from your own demand',
    'Purchase orders, approvals and PO insights',
    'Stock takes and inventory adjustments',
    'Automated purchase orders and Shopify Flow',
];

const REPLENISHMENT_UPSELL_DESCRIPTION =
    'Plan, raise and track purchase orders end to end with the Replenishment Center.';

const REPLENISHMENT_UPSELL_SECTION = {
    section: 'replenishment-upsell',
    title: 'Replenishment Center',
    icon: 'mdi-cart-plus',
    type: 'group',
    permissionRequired: true,
    // No paid_measures fallback, so this never resolves off-plan — which is
    // exactly what keeps the group collapsed and the modal reachable.
    permissionCode: FEATURES.BUYING_TABLE,
    lockInfo: {
        // The short form the rest of the menu uses. UpgradeModal maps it to
        // the full 'Replenishment Center' name and to the pricing catalog
        // entry (see upgradeModalCopy.ts), so the modal and /subscription
        // name the same plan; an unmapped value still renders, just without
        // catalog copy.
        requiredPackage: 'Replenishment',
        benefits: REPLENISHMENT_UPSELL_BENEFITS,
        tooltipText: 'Plan and manage purchasing with the Replenishment Center',
    } as LockInfo,
    benefits: REPLENISHMENT_UPSELL_BENEFITS,
    description: REPLENISHMENT_UPSELL_DESCRIPTION,
    items: gatherLinks([
        ['order', '/assistant/forecast'],
        ['order', '/assistant/plans'],
        ['order', '/buying-table'],
        ['order', '/buying-calendar'],
        ['track', '/purchase-orders'],
        ['track', '/purchase-orders/unapproved'],
        ['track', '/assistant/purchase-order/insights'],
        ['inventory', '/assistant/stock-take'],
        ['inventory', '/assistant/inventory-adjustments'],
        ['automate', '/automated-po-overview'],
        ['automate', '/shopify-flow'],
    ]),
};

export const analyticsV3 = {
    data: [
        pickSection('stocky-migration'),
        pickSection('today'),
        insightsWithoutDuplicateReorder(),
        PLAN_DEMAND_SECTION,
        // Suppliers is dropped from this tier's sidebar; /suppliers stays
        // routable and Settings already carries Supplier Parameters.
        pickLinks('inventory', [
            '/inventory-transfers/suggestions',
            '/settings/internal-inventory-transfer-settings',
        ]),
        pickLinks('automate', ['/schedules']),
        pickSection('my-workspace'),
        REPLENISHMENT_UPSELL_SECTION,
        pickSection('companion-apps'),
        pickSection('settings'),
    ],
};
